import i18n from "@/i18n/config";
import showdown from 'showdown';
import katex from 'katex';
import 'katex/dist/katex.min.css';

type HtmlParsedAnswer = {
    answerHtml: string;
    citations: string[];
    followupQuestions: string[];
};

const URL_REGEX = /(?<!\]\()(https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)\b)/g;
const MARKDOWN_LINK_REGEX = /\[([^\]]+)]\((\S+?)\)/g;
const FOLLOWUP_QUESTION_REGEX = /<<([^>]+)>>/g;
const TRIMMING_STRING = "######";
const PHONE_NUMBER_REGEX = /(\d{3})\s(\d{3})\s(\d{3})/g;
const INLINE_MATH_REGEX = /\$(.+?)\$/g;
const DISPLAY_MATH_REGEX = /\$\$(.+?)\$\$/gs;
const LATEX_ENV_REGEX = /\[([\s\S]+?)\]/gs;

const formatPhoneNumbers = (str: string): string => {
    return str.replace(PHONE_NUMBER_REGEX, '$1-$2-$3');
};

const replaceHyperlinks = (str: string): string => {
    return str.replace(URL_REGEX, '<a target="_blank" href="$1">$1</a>')
              .replace(MARKDOWN_LINK_REGEX, '<a target="_blank" href="$2">$1</a>');
};

const extractFollowUpQuestions = (answer: string, followupQuestions: string[]): string => {
    return answer.replace(FOLLOWUP_QUESTION_REGEX, (_, content) => {
        followupQuestions.push(content);
        return "";
    });
};

function renderLatexToHtml(html: string): string {
    // First render display math $$...$$
    html = html.replace(DISPLAY_MATH_REGEX, (_, mathContent) => {
        try {
            return katex.renderToString(mathContent, { displayMode: true, throwOnError: false });
        } catch {
            return _;
        }
    });

    // Then render [ ... ] math
    html = html.replace(LATEX_ENV_REGEX, (_, mathContent) => {
        try {
            return katex.renderToString(mathContent, { displayMode: true, throwOnError: false });
        } catch {
            return _;
        }
    });

    // Finally render inline math $...$
    html = html.replace(INLINE_MATH_REGEX, (_, mathContent) => {
        try {
            return katex.renderToString(mathContent, { displayMode: false, throwOnError: false });
        } catch {
            return _;
        }
    });

    return html;
}

export function parseAnswerToHtml(answer: string): HtmlParsedAnswer {
    const followupQuestions: string[] = [];
    const mdConverter = new showdown.Converter();
    let parsedAnswer = formatPhoneNumbers(answer);

    parsedAnswer = replaceHyperlinks(parsedAnswer);
    parsedAnswer = extractFollowUpQuestions(parsedAnswer, followupQuestions);

    if (parsedAnswer.includes(TRIMMING_STRING)) {
        parsedAnswer = parsedAnswer.split(TRIMMING_STRING)[0].trim();
    }

    parsedAnswer = mdConverter.makeHtml(parsedAnswer)
    parsedAnswer = renderLatexToHtml(parsedAnswer);

    return {
        answerHtml: parsedAnswer,
        citations: [],
        followupQuestions
    };
}

export function parseAnswerToText(answer: string): string {
    if (answer.includes(TRIMMING_STRING)) {
        answer = answer.split(TRIMMING_STRING)[0];
    }

    let modifiedAnswer = formatPhoneNumbers(answer);

    const hasLinks = URL_REGEX.test(answer) || MARKDOWN_LINK_REGEX.test(answer);
    modifiedAnswer = modifiedAnswer.replace(/Claro! |<<[^>]+>>|\[([^\]]+)]\(\S*?\)/g, "").trim();

    if (hasLinks) {
        modifiedAnswer += " " + i18n.t("settings.link");
    }

    return modifiedAnswer;
}
